import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth as useAuthHook } from '../hooks/useAuth';
import { API_URL } from '../utils';

interface User {
  language: string;
  // Add other user properties here
}

interface AuthContextType {
  isAuthenticated: boolean;
  idToken: string | null;
  user: User | null;
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
  handleLogout: () => void;
  updateUser: (data: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { 
    idToken, 
    fetchWithAuth, 
    handleLogout 
  } = useAuthHook();
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (idToken) {
        try {
          const response = await fetchWithAuth(`${API_URL}/user/profile`);
          const data = await response.json();
          setUser(data);
        } catch (error) {
          console.error('Failed to fetch user profile', error);
        }
      }
    };
    fetchUser();
  }, [idToken, fetchWithAuth]);

  const updateUser = (data: Partial<User>) => {
    setUser(prevUser => prevUser ? { ...prevUser, ...data } : null);
  };

  const value = {
    isAuthenticated: !!idToken,
    idToken,
    user,
    fetchWithAuth,
    handleLogout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 